import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, BookOpen, ShoppingCart, DollarSign, Plus, TrendingUp,
  Eye, ArrowUpRight, Clock, GraduationCap, FileText, Video, ExternalLink
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const PIE_COLORS = ["#0284C7", "#7C3AED", "#059669", "#D97706", "#DC2626", "#2563EB", "#64748B", "#0891B2"];
const TOOLTIP_STYLE = { background: "#111827", border: "none", color: "#FFF", fontSize: 12 };
const AXIS_TICK_SM = { fontSize: 11, fill: "#94A3B8" };
const AXIS_TICK = { fontSize: 12, fill: "#94A3B8" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [examData, setExamData] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [upcomingLive, setUpcomingLive] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/revenue-chart"),
      api.get("/analytics/popular-exams"),
      api.get("/analytics/recent-enrollments"),
      api.get("/admin/upcoming-live").catch(() => ({ data: [] })),
    ]).then(([ov, rev, exams, enroll, live]) => {
      setOverview(ov.data);
      setRevenueData(rev.data);
      setExamData(exams.data);
      setEnrollments(enroll.data);
      setUpcomingLive(live.data);
    }).catch((err) => console.error(err)).finally(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><div className="admin-text-secondary py-12 text-center">Loading dashboard...</div></AdminLayout>;

  const stats = overview ? [
    { label: "Total Courses", value: overview.total_courses, icon: BookOpen, trend: "+12%", color: "bg-[#0284C7]", lightBg: "bg-blue-50" },
    { label: "Total Students", value: overview.total_users, icon: Users, trend: "+8%", color: "bg-[#7C3AED]", lightBg: "bg-purple-50" },
    { label: "Revenue", value: `₹${(overview.total_revenue || 0).toLocaleString()}`, icon: DollarSign, trend: "+23%", color: "bg-[#059669]", lightBg: "bg-green-50" },
    { label: "Total Orders", value: overview.total_orders, icon: ShoppingCart, trend: "+5%", color: "bg-[#D97706]", lightBg: "bg-amber-50" },
  ] : [];

  const miniStats = overview ? [
    { label: "Videos", value: overview.total_videos, icon: FileText },
    { label: "Chapters", value: overview.total_chapters, icon: BookOpen },
    { label: "Instructors", value: overview.total_instructors, icon: GraduationCap },
    { label: "Pending Orders", value: overview.pending_orders, icon: Clock },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Dashboard
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">Welcome back! Here's your overview.</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-add-course-btn"
              onClick={() => navigate("/admin/courses")}
              className="bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none hover:bg-[#0369A1]"
            >
              <Plus size={14} /> New Course
            </button>
            <button
              data-testid="admin-pending-payments-btn"
              onClick={() => navigate("/admin/orders")}
              className="admin-card border font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none admin-text"
            >
              <Eye size={14} /> Pending Payments
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="admin-dashboard-stats">
          {stats.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="admin-card border p-5 relative overflow-hidden">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 ${card.color} text-white flex items-center justify-center`}>
                    <Icon size={18} />
                  </div>
                  <span className="flex items-center gap-0.5 text-xs font-bold text-green-500">
                    <TrendingUp size={12} />
                    {card.trend}
                  </span>
                </div>
                <p className="text-2xl font-black admin-text">{card.value}</p>
                <p className="text-xs admin-text-secondary font-medium mt-0.5">{card.label}</p>
              </div>
            );
          })}
        </div>

        {/* Mini Stats Row */}
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {miniStats.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="admin-card border px-3 py-3 flex items-center gap-2">
                <Icon size={14} className="text-[#0284C7]" />
                <div>
                  <p className="text-base font-black admin-text">{item.value}</p>
                  <p className="text-[10px] admin-text-secondary">{item.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Revenue Chart */}
          <div className="lg:col-span-2 admin-card border p-5">
            <h2 className="text-base font-bold admin-text mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Revenue Overview
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0284C7" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0284C7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={AXIS_TICK_SM} />
                  <YAxis tick={AXIS_TICK_SM} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value) => [`₹${value}`, "Revenue"]}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#0284C7" strokeWidth={2} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Popular Exams Pie Chart */}
          <div className="admin-card border p-5">
            <h2 className="text-base font-bold admin-text mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Popular Exams
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={examData}
                    cx="50%"
                    cy="45%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {examData.map((entry, index) => (
                      <Cell key={`cell-${entry.name || index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Upcoming Live Sessions */}
        {upcomingLive.length > 0 && (
          <div className="admin-card border overflow-hidden mb-6">
            <div className="px-5 py-4 flex items-center justify-between border-b admin-table-row">
              <h2 className="text-base font-bold admin-text flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                <Video size={18} className="text-red-500" /> Upcoming Live
              </h2>
              <button onClick={() => navigate("/admin/live")} className="text-xs font-bold text-[#0284C7] flex items-center gap-1 transition-none">
                View All <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="divide-y admin-table-row">
              {upcomingLive.slice(0, 3).map(s => (
                <div key={s.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Video size={16} className="text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium admin-text truncate">{s.title}</p>
                    <p className="text-[10px] admin-text-secondary">{s.course_title} &middot; {s.scheduled_date} at {s.scheduled_time}</p>
                  </div>
                  {s.meet_link && (
                    <a href={s.meet_link.startsWith("http") ? s.meet_link : "https://" + s.meet_link} target="_blank" rel="noopener noreferrer" className="text-[#0284C7] p-2">
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Latest Enrollments Table */}
        <div className="admin-card border overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b admin-table-row">
            <h2 className="text-base font-bold admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Latest Enrollments
            </h2>
            <button
              onClick={() => navigate("/admin/orders")}
              className="text-xs font-bold text-[#0284C7] flex items-center gap-1 transition-none"
            >
              View All <ArrowUpRight size={12} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="enrollments-table">
              <thead>
                <tr className="border-b admin-table-row">
                  <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Student</th>
                  <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Course</th>
                  <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Category</th>
                  <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Amount</th>
                  <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Date</th>
                  <th className="text-center px-5 py-3 text-[10px] font-bold uppercase tracking-wider admin-text-secondary">Status</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((e) => (
                  <tr key={e.id} className="border-b admin-table-row">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium admin-text">{e.user_name || "—"}</p>
                      <p className="text-[10px] admin-text-secondary">{e.user_email || ""}</p>
                    </td>
                    <td className="px-5 py-3 text-sm admin-text">{e.course_title || "—"}</td>
                    <td className="px-5 py-3">
                      <span className="text-[10px] font-bold bg-[#0284C7]/10 text-[#0284C7] px-2 py-0.5">
                        {e.course_category || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm font-bold admin-text text-right">₹{e.amount}</td>
                    <td className="px-5 py-3 text-xs admin-text-secondary text-right">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${
                        e.status === "success" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {e.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {enrollments.length === 0 && (
                  <tr><td colSpan="6" className="px-5 py-8 text-center admin-text-secondary text-sm">No enrollments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
